/**
 * Captures Chromium's `beforeinstallprompt` event.
 *
 * The event fires very early — often before React has mounted — and is only usable once.
 * If nobody calls `preventDefault()` on it, Chrome shows its own mini infobar and we lose
 * the chance to ask at a better moment, so the listener is installed at module load.
 */

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
  prompt: () => Promise<void>
}

type Listener = () => void

let deferredPrompt: BeforeInstallPromptEvent | undefined
const listeners = new Set<Listener>()

const notify = () => {
  for (const listener of listeners) {
    listener()
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    deferredPrompt = undefined
    notify()
  })
}

export const canPromptInstall = (): boolean => Boolean(deferredPrompt)

/** Subscribe to changes in install availability. Returns an unsubscribe function. */
export const onInstallPromptChange = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Shows the browser install dialog. Resolves to true when the user accepted. */
export const showInstallPrompt = async (): Promise<boolean> => {
  if (!deferredPrompt) {
    return false
  }
  const prompt = deferredPrompt
  // The event cannot be reused, whatever the outcome.
  deferredPrompt = undefined
  notify()
  await prompt.prompt()
  const { outcome } = await prompt.userChoice
  return outcome === 'accepted'
}
